
import Page from '../Page.vue';
import { notificationStore, sportStore } from '@/store/index';
import { Component, Mixins } from 'vue-property-decorator';
import { VuetifyMixin, PaginatedTableMixin, CoachRoutingMixin, BAIconsMixin, StringsMixin, SportsSelectorMixin } from '@/mixins';
import { RepositoryQuery, QueryOptions, PaginatedResponse, SportName } from '@/../types';
import { DataTableHeader } from 'vuetify';
import { AthleteProfileModel, SportModel } from '@/models';
import { TeamAthleteProgress, AthleteProgressAbbreviations, AthleteProgressFields } from '@/models/team'
import { RecruitingProfileModel } from '@/models/athlete/RecruitingProfileModel'
import { athleteApi } from '@/api/AthleteApi';

@Component({
	components: {
		Page,
	}
})
export default class AthleteAdminDashboard extends Mixins(VuetifyMixin, StringsMixin, PaginatedTableMixin, CoachRoutingMixin, BAIconsMixin, SportsSelectorMixin){
	sport: SportName;
	currentSport: SportModel;

	mounted() {
		this.tableOptions.itemsPerPage = 25;
		this.tableOptions.sortBy = ['lastName'];
		this.tableOptions.sortDesc = [false];
		this.localForagePersistFields = [['search', ''],['tableOptions.page', 1],['tableOptions.itemsPerPage', 25]];
	}

	onSelectSport() {
		this.currentSport = undefined;
		this.loadTable();
	}

	async loadTable(): Promise<void> {
		try {			
			this.isLoading = true;
			this.isLoaded = false;
			if( this.IsNotEmpty(this.sport) ) this.currentSport = await sportStore.findSportByName(this.sport);
			const query: RepositoryQuery<AthleteProfileModel> = this.TableQuery<AthleteProfileModel>(this.search, ['lastName', 'firstName', 'email', 'id', 'shortId', 'tags']);
			query.$match = {...query.$match, ...{ deleted: this.showDeleted? true : {$any:[false, undefined, null]} }};
			if( this.IsNotEmpty(this.currentSport) ) query.$match = {...query.$match, ...{ athleteSports: this.currentSport.id}};
			const options: QueryOptions = this.TableQueryOptions;
			const response: PaginatedResponse<AthleteProfileModel> = await athleteApi.queryAll(query, options);
			this.dataItems = response.docs;
			this.dataItemsCount = response.total;
			this.isLoaded = true;
			this.isLoading = false;
		} catch(e) {
			notificationStore.pushSnackbarError({message: `Error loading athletes: ${e}`});
		}
	}

	get TableHeaders(): Array<DataTableHeader<any>> {
		let headers: Array<DataTableHeader<any>> = [
			{ text: 'Name', value: 'lastName', sortable: false, },
			{ text: '', value: 'share', sortable: false },
			{ text: 'email', value: 'email', sortable: false, },
			{ text: 'athleteId', value: 'id', sortable: false, },			
		];
		if( this.IsLargeScreen ) {
			headers.push({ text: 'shortId', value: 'shortId', sortable: false }),
			headers.push({ text: 'gen', value: 'gender', sortable: false }),
			headers.push({ text: 'DOB', value: 'birthDate', sortable: false }),
			headers.push({ text: 'sport', value: 'sport', sortable: false });
			headers.push({ text: '', value: 'data-table-expand', sortable: false });
		}
		return headers;
	}
	get TableLoading(): boolean {
		// mixin user can override this function if something other than isLoading is used
		return this.isLoading || this.isAthleteDeleting;
	}

	duplicateLoading: boolean = false;
	duplicates: Array<AthleteProfileModel> = [];
	noDuplicatesFound: boolean = false;
	primaryLoaded: boolean = false;
	primaryAccount: AthleteProfileModel = undefined;
	mergedDuplicates: AthleteProfileModel = undefined;
	readyToMerge: boolean = false;
	dateKeys: Array<string> = ['birthDate','deleted_at','last_login'];
	mergeData: Array<Record<string,any>> = [];
	showMergeDuplicates: boolean = false;
	mergeSkipLoading: boolean = false;
	detailsLoading: boolean = false;
	showAthleteDetail: boolean = false;
	showDeleted: boolean = false;

	async onFindDuplicates(authId: string = undefined) {
		this.duplicateLoading = true;
		this.findDuplicates(authId);
	}
	async findDuplicates(authId: string = undefined) {
		if( this.IsEmpty(authId) ) this.duplicates = await athleteApi.findDuplicates(this.IsNotEmpty(this.search)? this.search : '');
		else this.duplicates = await athleteApi.findUserDuplicates(authId);
		
		this.findDuplicatesComplete();
	}
	async findDuplicatesComplete() {
		if( this.IsEmpty(this.duplicates) ) {
			this.noDuplicatesFound = true;
		} else {
			this.duplicates.forEach(d => {
				d.useInMerge = true;
			});
			let primary: AthleteProfileModel = undefined;
			for( const d of this.duplicates ) {
				if( this.IsEmpty(primary) || (d.ProgressValue > primary.ProgressValue) ) {
					primary = d;
				}
			}
			this.onSelectPrimary(primary.id);
		}
		this.showAthleteDetail = true;
		this.duplicateLoading = false;
	}
	get HasDuplicates(): boolean {
		if( this.duplicateLoading ) return false;
		return this.IsNotEmptyArray(this.duplicates);
	}
	get DuplicatesName(): string {
		if( !this.HasDuplicates ) return 'unknown';
		
		if( this.IsNotEmpty(this.primaryAccount) ) {
			if( this.IsNotEmpty(this.primaryAccount.firstName) || this.IsNotEmpty(this.primaryAccount.lastName) ) {
				return this.FullName(this.primaryAccount.firstName, this.primaryAccount.lastName);
			}
		}
		for( const dup of this.duplicates ) {
			if( this.IsNotEmpty(dup.firstName) || this.IsNotEmpty(dup.lastName) ) {
				return this.FullName(dup.firstName, dup.lastName);
			}
		}
		return 'unknown'
	}
	mergedProgress: TeamAthleteProgress;
	dupProgress: Array<TeamAthleteProgress>;
	private async PrepareMerge() {		
		this.readyToMerge = false;
		if( this.IsEmpty(this.primaryAccount) ) return;

		this.dupProgress = await Promise.all(
			this.duplicates.map(async(d) => {
				const recruitingProfile: RecruitingProfileModel = await athleteApi.getAthleteRecruitingProfile(d.id, {as : this.getAppType} );
				const progress: TeamAthleteProgress = d.InitProgress(recruitingProfile);
				return progress;
			}
		));
		this.mergedDuplicates = new AthleteProfileModel(this.primaryAccount);
		if( this.IsEmpty(this.mergedDuplicates.altEmail)) this.mergedDuplicates.altEmail = [];
		this.duplicates.forEach( d => {
			if( this.IsDupPrimary(d.id) ) return;
			if( !d.useInMerge ) return;

			for( const key of Object.keys(d) ) {
				if( this.IsNotEmpty(d[key]) ) {
					if( this.IsEmpty(this.mergedDuplicates[key]) ) {
						this.mergedDuplicates[key] = d[key];
					}
					if( this.dateKeys.includes(key)) {
						if( d[key] > this.mergedDuplicates[key] ) this.mergedDuplicates[key] = d[key];
					}
				}
			}

			if( this.mergedDuplicates.email != d.email && !this.mergedDuplicates.altEmail.includes(d.email) ) this.mergedDuplicates.altEmail.push(d.email);
		})
		await athleteApi.UpdateSports(this.mergedDuplicates, false);

		// progress
		this.mergedProgress = this.mergedDuplicates.InitProgress();
		this.duplicates.forEach( d => {
			if( !this.mergedProgress.hasVerifiedAssessment ) this.mergedProgress.hasVerifiedAssessment = d.progress?.hasVerifiedAssessment;
			if( !this.mergedProgress.mindsetArchetype ) this.mergedProgress.mindsetArchetype = d.progress?.mindsetArchetype;
			if( !this.mergedProgress.hasScoutingReport ) this.mergedProgress.hasScoutingReport = d.progress?.hasScoutingReport;
		})

		// merge data to display
		const keysToShow: Array<{key: string, label: string}> = [
			{ key:'firstName', label:'First Name' },
			{ key:'lastName', label:'Last Name' },
			{ key:'email', label:'Email' },
			{ key:'altEmail', label:'Alt Email' },
			{ key:'id', label:'id' },
			{ key:'birthDate', label:'Date of Birth' },
			{ key:'gender', label:'Gender' },
			{ key:'height', label:'Height' },
			{ key:'weight', label:'Weight' },
			{ key:'primaryPosition', label:'Position' },
			{ key:'gradYear', label:'Grad Year' },
			{ key:'gpa', label:'GPA' },
		];

		this.mergeData = keysToShow.map(show => {
			let value = this.mergedDuplicates[show.key];
			
			if( show.key === "id" ) value = this.SnipAthleteId(this.mergedDuplicates.id);
			else if( show.key === "email" ) value = this.SnipEmail(this.mergedDuplicates.Email);
			else if( show.key === "height" ) value = this.HeightInFt(this.mergedDuplicates.height);
			else if( show.key === "weight" ) value = this.WeightInLbs(this.mergedDuplicates.weight);
			else if( show.key === "birthDate" ) value = this.mergedDuplicates.BirthDate;
			else if( show.key === "gradYear" ) value = this.mergedDuplicates.GradYear?.toFixed(0);
			else if( show.key === "gpa" ) value = this.mergedDuplicates.GPA;

			return { key: show.label, value };
		});

		this.readyToMerge = this.IsNotEmpty(this.primaryAccount) && (this.MergeCount > 1);
	}
	onClearDuplicates() {
		this.cleanupMerge();
	}
	cleanupMerge() {
		this.duplicates = [];
		this.primaryAccount = undefined;
		this.mergedDuplicates = undefined;
		this.noDuplicatesFound = false;
		this.readyToMerge = false;
		this.primaryLoaded = false;
		this.updateFilter();
	}
	async onSkipMergeDuplicates() {
		this.mergeSkipLoading = true;
		await athleteApi.mergeSkipAccounts(this.duplicates.map(d => d.id));
		this.mergeSkipLoading = false;
		this.cleanupMerge();
	}
	async onMergeDuplicates() {
		if( !this.readyToMerge ) {
			notificationStore.pushSnackbarError({message: 'Please select the primary account'}) 
			return;
		}
		this.PrepareMerge();
		this.showMergeDuplicates = true;
	}
	IsDupPrimary( athleteId: string ): boolean {
		if( this.IsEmpty(this.primaryAccount) ) return false;
		return athleteId === this.primaryAccount.id;
	}
	get DuplicatesCount(): number {
		return this.duplicates.length;
	}
	get DuplicatesToMerge(): Array<AthleteProfileModel> {
		return this.duplicates.filter(d => d.useInMerge);
	}
	get MergeCount(): number {
		return this.DuplicatesToMerge.length;
	}
	async MergeDuplicatesConfirm() {
		this.showMergeDuplicates = false;
		notificationStore.pushSnackbarWarning({message:`Merging User Accounts...`});

		// the first id must be that of the primary account
		const mergeIds = [this.primaryAccount.id];
		// add the selected duplicates
		mergeIds.push(...this.DuplicatesToMerge.map(d => d.id));
		await athleteApi.mergeAccounts(mergeIds);
		this.cleanupMerge();
	}
	async onSelectPrimary(athleteId: string) {
		this.primaryLoaded = false;
		this.primaryAccount = undefined;

		this.duplicates.forEach(d => { d.primary = ( d.id === athleteId ) });
		const primary = this.duplicates.find(d => d.primary === true);
		if( this.IsNotEmpty(primary) ) primary.useInMerge = true;
		this.primaryAccount = new AthleteProfileModel(primary);
		await this.PrepareMerge();
		this.primaryLoaded = true;
	}
	async onUseInMerge( athleteId: string ) {
		this.primaryLoaded = false;
		await this.PrepareMerge();
		this.primaryLoaded = true;
	}
	async onToggleAthleteDetail() {
		this.detailsLoading = true;
		this.showAthleteDetail = !this.showAthleteDetail;
		this.detailsLoading = false;
	}
	AthleteProgressData(progress: TeamAthleteProgress, abbreviatedLabels: boolean = true): Array<Record<string,any>> {
		return this.KeysToShowAthleteProgress.map(k => {
			const key = abbreviatedLabels? AthleteProgressAbbreviations[k] : AthleteProgressFields[k];
			const value: any = this.IsNotEmpty(progress)? progress[k] : undefined;
			return {key, value};
		});
	}

	get MergeHeaders(): Array<DataTableHeader<any>> {
		let headers: Array<DataTableHeader<any>> = [
			{ text: 'Key', value: 'key', sortable: false },
			{ text: 'Value', value: 'value', sortable: false },
		];
		return headers;
	}
	get KeysToShowAthleteProgress(): Array<string> {
		return [
			'hasPhoto',
			'hasEmail',
			'hasPhone',
			'hasInsta',
			'hasTwitter',
			'hasBio',
			'hasDOB',
			'hasHeight',
			'hasWeight',
			'hasDominant',
			'hasLocation',
			'hasHighlights',
			'hasGradYear',
			'hasGPA',
			'hasSAT',
			'hasVerifiedAssessment',
			'mindsetArchetype',
			'hasScoutingReport',
			'commitment',
		]
	}

	onToggleShowDeleted() {
		this.showDeleted = !this.showDeleted;
		this.updateFilter();
	}
	athleteToDelete: AthleteProfileModel;
	showAthleteDeleteDialog: boolean = false;
	isAthleteDeleting: boolean = false;
	get AthleteToDeleteName(): string {
		if( this.IsEmpty(this.athleteToDelete) ) return 'Athlete';
		if( this.athleteToDelete.FullName.length < 3 ) return `Athlete ${this.SnipAthleteId(this.athleteToDelete.id)}`;
		return this.athleteToDelete.FullName;
	}
	onDeleteAthlete(athlete: AthleteProfileModel) {
		this.athleteToDelete = athlete;
		this.showAthleteDeleteDialog = true;
	}
	async onDeleteAthleteConfirm() {
		this.isAthleteDeleting = true;
		await athleteApi.patch({
				id: this.athleteToDelete.id,
				deleted: true,
			});
		const index = this.dataItems.findIndex(i => i.id === this.athleteToDelete.id);
		if( index >= 0 ) this.dataItems.splice(index, 1);
		this.showAthleteDeleteDialog = false;
		this.athleteToDelete = undefined;
		this.isAthleteDeleting = false;
	}
	onDeleteAthleteCancel() {
		this.showAthleteDeleteDialog = false;
		this.athleteToDelete = undefined;
	}

	async onUndeleteAthlete(athlete: AthleteProfileModel) {
		await athleteApi.undelete(athlete.id);
		this.updateFilter();
	}
}
